// src/services/VoiceCallService.js
//
// Phase 48: Simplified — incoming calls use conference model (Firestore-driven).
// The Device is only used for outbound connect() (dialer + conference join).
// No passive incoming event handling.
import { ref } from 'vue'
import { getTwilioDeviceClass } from '@/utils/twilioDevice'
import { hasActiveCallElsewhere, onExternalOwnerChange } from '@/services/activeCallTabCoordinator'

const CONNECT_TIMEOUT_MS = 15000

// Exponential backoff for automatic re-registration after the Twilio Device
// drops to `unregistered`. Triggers in production: tab backgrounded in Edge
// (aggressive throttling), brief network/VPN blip, OS sleep/resume, a peer
// tab booting under the same identity. Without this, the user has to refresh
// to send/receive calls again — and agents won't notice they're offline.
const REG_BACKOFF_MS = [1000, 2000, 4000, 8000, 16000]

class VoiceCallService {
  constructor() {
    this.device = null
    this.currentConnection = null
    this.hasLocalConnection = ref(false)
    this._timeoutId = null
    this._regAttempt = 0
    this._regTimer = null
    // Token provider injected by initDevice — Twilio access tokens have a TTL
    // (~1h); without refreshing, an idle tab's device dies permanently
    // (re-register loops AccessTokenExpired/Invalid with the stale token) and
    // answerCall hard-refuses. See _refreshToken / tokenWillExpire below.
    this._fetchToken = null
    this._refreshingToken = false
    // Reactive flag mirrored from the Twilio Device's `registered`/`unregistered`
    // events. Consumers can use this directly instead of polling `device.state`.
    this.isDeviceReady = ref(false)
    // A device belongs to the signed-in STAFF session that created it. App.vue
    // injects the provider (staff uid, or null when nobody or a non-staff account
    // is signed in); the key is captured at initDevice. Until 2026-09-15 a device
    // outlived sign-out: its token refresh and re-register paths fetched tokens
    // for whoever signed in next, a portal customer included
    // (docs/bugs/voice_device_survives_sign_out_BUG.md). No provider = unmanaged.
    this._sessionKeyProvider = null
    this._deviceSessionKey = null
    this._audioOwnership = null
    this._connectAttempt = null
    this._timeoutAttempt = null
  }

  setAudioOwnership(ownership) {
    this._audioOwnership = ownership
  }

  /** App.vue: `() => staff uid | null`. The device only works for that session. */
  setSessionKeyProvider(provider) {
    this._sessionKeyProvider = typeof provider === 'function' ? provider : null
  }

  _currentSessionKey() {
    if (!this._sessionKeyProvider) return null
    try {
      return this._sessionKeyProvider() || null
    } catch {
      return null
    }
  }

  // True while the signed-in staff session is the one that created the device.
  // Sign-out, a non-staff account or a different staff member all read false,
  // and every path that would fetch a token or re-register checks it first.
  _sessionIsCurrent() {
    if (!this._sessionKeyProvider) return true
    const key = this._currentSessionKey()
    return !!key && key === this._deviceSessionKey
  }

  _clearRegTimer() {
    if (this._regTimer) {
      clearTimeout(this._regTimer)
      this._regTimer = null
    }
  }

  // Fetch a fresh access token (when a provider was injected) and hand it to
  // the Device. Safe to call opportunistically — failures are logged and the
  // device just keeps its current token.
  async _refreshToken() {
    if (!this._fetchToken || !this.device || this._refreshingToken) return false
    if (!this._sessionIsCurrent()) return false
    const device = this.device
    this._refreshingToken = true
    try {
      const token = await this._fetchToken()
      // The session can end, or the device be replaced, while the fetch is out.
      if (token && this.device === device && this._sessionIsCurrent()) {
        device.updateToken(token)
        return true
      }
    } catch (e) {
      console.warn('[VoiceCallService] Token refresh failed:', e?.message || e)
    } finally {
      this._refreshingToken = false
    }
    return false
  }

  _scheduleReregister() {
    this._clearRegTimer()
    if (!this.device || !this._sessionIsCurrent()) return
    if (this._regAttempt >= REG_BACKOFF_MS.length) {
      console.warn(
        '[VoiceCallService] Max re-register attempts reached; giving up until ensureRegistered()/initDevice()'
      )
      return
    }
    const device = this.device
    const delay = REG_BACKOFF_MS[this._regAttempt++]
    this._regTimer = setTimeout(async () => {
      this._regTimer = null
      if (this.device !== device || !this._sessionIsCurrent()) return
      // If a peer tab now hosts an active call, defer to it — Twilio only
      // allows one registration per identity. Reschedule to check again.
      if (hasActiveCallElsewhere()) {
        this._scheduleReregister()
        return
      }
      // Registering with an expired token loops 20104/20101 forever — refresh
      // first when a provider is available (cheap when the token is still good;
      // updateToken on a registered-capable device is a no-op-ish reissue).
      await this._refreshToken()
      if (this.device !== device || !this._sessionIsCurrent()) return
      try {
        const p = device.register?.()
        if (p && typeof p.catch === 'function') {
          p.catch(e => {
            console.warn('[VoiceCallService] Re-register attempt failed:', e?.message || e)
            this._scheduleReregister()
          })
        }
      } catch (e) {
        console.warn('[VoiceCallService] Re-register threw synchronously:', e?.message || e)
        this._scheduleReregister()
      }
    }, delay)
  }

  // Wake-path recovery: called on tab-visible/online. If the device gave up
  // (backoff exhausted while the tab was asleep) or sits unregistered on a
  // stale token, this re-arms it with a fresh token. No-op when already
  // registered, mid-backoff, or when a peer tab owns the registration.
  async ensureRegistered() {
    if (!this.device || this.isDeviceReady.value) return
    if (!this._sessionIsCurrent()) return
    if (this._regTimer) return // backoff in flight — let it run
    if (hasActiveCallElsewhere()) return
    const device = this.device
    this._regAttempt = 0
    await this._refreshToken()
    if (this.device !== device || !this._sessionIsCurrent()) return
    try {
      await device.register()
    } catch (e) {
      console.warn('[VoiceCallService] ensureRegistered register failed:', e?.message || e)
      this._scheduleReregister()
    }
  }

  /**
   * Initialize Twilio Device (needed for outbound connect — dialer + conference join).
   * No incoming event handling — inbound calls use Firestore awareness + conference join.
   * `fetchToken` (async → token string) keeps the device alive past the access
   * token's TTL: tokenWillExpire refreshes proactively, and re-register paths
   * refresh before retrying instead of looping on the stale token.
   */
  async initDevice(token, { onDeviceError, fetchToken } = {}) {
    // No device for a session that is not staff (voice is a staff surface).
    if (this._sessionKeyProvider && !this._currentSessionKey()) {
      console.warn('[VoiceCallService] initDevice skipped: no signed-in staff session')
      return
    }
    const requestedSessionProvider = this._sessionKeyProvider
    const requestedSessionKey = this._currentSessionKey()
    const Device = await getTwilioDeviceClass()
    if (
      requestedSessionProvider !== this._sessionKeyProvider ||
      (requestedSessionProvider && requestedSessionKey !== this._currentSessionKey())
    )
      return
    this.hangup()
    try {
      this.device?.destroy?.()
    } catch {
      // ignore — best-effort destroy before reinitializing the device
    }
    this._clearRegTimer()
    this._regAttempt = 0
    this._fetchToken = typeof fetchToken === 'function' ? fetchToken : null
    this._deviceSessionKey = this._currentSessionKey()

    this.device = new Device(token, {
      logLevel: 'error',
      codecPreferences: ['opus', 'pcmu'],
      allowIncomingWhileBusy: false,
    })

    this.isDeviceReady.value = false

    this.device.on('error', err => onDeviceError?.(err))

    // Proactive refresh — the SDK emits this shortly before the access token
    // expires. Without it the device unregisters at TTL and every re-register
    // with the stale token fails (AccessTokenExpired → AccessTokenInvalid).
    this.device.on('tokenWillExpire', () => {
      this._refreshToken()
    })

    this.device.on('registered', () => {
      console.log('[VoiceCallService] Twilio Device Registered & Ready')
      this.isDeviceReady.value = true
      this._regAttempt = 0
      this._clearRegTimer()
    })

    this.device.on('unregistered', () => {
      console.warn('[VoiceCallService] Twilio Device Unregistered')
      this.isDeviceReady.value = false
      // Twilio only allows one registration per identity. If a peer tab is
      // hosting an active call, it's the one we just got kicked off for —
      // don't fight it. Otherwise schedule an auto re-register with backoff.
      if (hasActiveCallElsewhere()) return
      this._scheduleReregister()
    })

    // Incoming events are no longer handled — conference model uses device.connect().
    // Log for debugging only.
    this.device.on('incoming', connection => {
      console.log(
        '[VoiceCallService] Incoming event received (ignored — conference model)',
        connection.parameters
      )
      try {
        connection.reject()
      } catch {
        // ignore — conference model doesn't use incoming events; reject is best-effort
      }
    })

    try {
      await this.device.register()
    } catch (e) {
      console.warn('[VoiceCallService] Registration warning:', e)
    }
  }

  _startTimeout(onExpire) {
    this._clearTimeout()
    this._timeoutId = setTimeout(() => {
      this._timeoutId = null
      onExpire?.()
    }, CONNECT_TIMEOUT_MS)
  }

  _clearTimeout() {
    if (this._timeoutId) {
      clearTimeout(this._timeoutId)
      this._timeoutId = null
    }
  }

  async connect(params, listeners = {}) {
    if (!this.device) throw new Error('Twilio Device not initialized')
    if (hasActiveCallElsewhere()) throw new Error('Finish the call in the other tab first.')
    if (this._connectAttempt && !this._connectAttempt.terminal)
      throw new Error('The previous audio session is still closing.')
    const attempt = {
      device: this.device,
      sessionKey: this._deviceSessionKey,
      cancelled: false,
      pendingSDK: false,
      connection: null,
      lease: null,
      terminal: false,
    }
    this._connectAttempt = attempt
    const current = () =>
      this._connectAttempt === attempt &&
      !attempt.cancelled &&
      this.device === attempt.device &&
      this._deviceSessionKey === attempt.sessionKey &&
      this._sessionIsCurrent() &&
      !hasActiveCallElsewhere()
    const clearTimer = () => {
      if (this._timeoutAttempt === attempt) {
        this._timeoutAttempt = null
        this._clearTimeout()
      }
    }
    const terminal = (kind, args = []) => {
      if (attempt.terminal) return
      const notify = this._connectAttempt === attempt
      clearTimer()
      attempt.terminal = true
      if (this.currentConnection === attempt.connection) {
        this.currentConnection = null
        this.hasLocalConnection.value = false
      }
      if (this._connectAttempt === attempt) this._connectAttempt = null
      attempt.lease?.release()
      if (notify && !attempt.failure && kind === 'disconnect') listeners.onDisconnect?.(...args)
      if (notify && !attempt.failure && kind === 'cancel') listeners.onCancel?.(...args)
    }
    attempt.finish = terminal
    try {
      if (this._audioOwnership)
        attempt.lease = await this._audioOwnership.acquireCompany({ stop: () => this.hangup() })
      if (!current()) throw new Error('The call was cancelled before audio connected.')
      this._timeoutAttempt = attempt
      this._startTimeout(() => {
        if (this._connectAttempt !== attempt || attempt.terminal) return
        attempt.cancelled = true
        attempt.failure = 'timeout'
        this._stopMediaAttempt(attempt)
        listeners.onTimeout?.()
      })
      attempt.pendingSDK = true
      let conn
      try {
        conn = await attempt.device.connect({ params })
      } finally {
        attempt.pendingSDK = false
      }
      attempt.connection = conn
      const accepted = (...args) => {
        if (!current()) {
          this._stopMediaAttempt(attempt)
          return
        }
        if (attempt.accepted) return
        attempt.accepted = true
        clearTimer()
        listeners.onAccept?.(...args)
      }
      conn.on('accept', accepted)
      conn.on('disconnect', (...args) => terminal('disconnect', args))
      conn.on('cancel', (...args) => terminal('cancel', args))
      conn.on('reject', (...args) => terminal('cancel', args))
      conn.on('error', (...args) => {
        if (this._connectAttempt !== attempt || attempt.terminal) return
        clearTimer()
        attempt.cancelled = true
        attempt.failure = 'error'
        this._stopMediaAttempt(attempt)
        listeners.onError?.(...args)
      })
      if (!current()) {
        this._stopMediaAttempt(attempt)
        throw new Error('The call was cancelled before audio connected.')
      }
      if (conn.status?.() === 'closed') {
        terminal('disconnect')
        throw new Error('The call ended before audio connected.')
      }
      this.currentConnection = conn
      this.hasLocalConnection.value = true
      if (conn.status?.() === 'open') accepted()
      return conn
    } catch (error) {
      clearTimer()
      attempt.cancelled = true
      this._stopMediaAttempt(attempt)
      if (!attempt.pendingSDK && !attempt.connection) terminal()
      throw error
    }
  }

  _stopMediaAttempt(attempt) {
    if (!attempt || attempt.terminal) return
    try {
      attempt.connection?.mute?.(true)
      attempt.connection?.disconnect?.()
    } catch {
      /* retain the lease until terminal proof */
    }
    if (attempt.pendingSDK) {
      try {
        attempt.device.disconnectAll?.()
      } catch {
        /* late SDK result is still owned */
      }
    }
    if (!attempt.pendingSDK && attempt.connection?.status?.() === 'closed')
      attempt.finish?.('disconnect')
  }

  hangup() {
    this._clearTimeout()
    const attempt = this._connectAttempt
    if (attempt) {
      attempt.cancelled = true
      this._stopMediaAttempt(attempt)
    }
    try {
      this.currentConnection?.disconnect?.()
    } catch {
      // ignore — best-effort disconnect on hangup
    }
    this.currentConnection = null
    this.hasLocalConnection.value = false
  }

  /**
   * Tear the device down for good, on sign-out, an account switch, or voice being
   * turned off. hangup() is the caller's job for an active call in THIS tab.
   *
   * The device is detached at once: no listeners, no token provider, no backoff,
   * so it can never refresh or re-register again. The Twilio `destroy()` itself
   * (which unregisters) waits while ANOTHER tab hosts an active call. A
   * registration change for the same identity can drop that tab's audio
   * (activeCallTabCoordinator.js), so the destroy runs when its call ends.
   */
  destroyDevice() {
    this.hangup()
    this._clearRegTimer()
    this._clearTimeout()
    this._regAttempt = 0
    this._fetchToken = null
    this._refreshingToken = false
    this._deviceSessionKey = null
    this.isDeviceReady.value = false
    const device = this.device
    this.device = null
    this.currentConnection = null
    this.hasLocalConnection.value = false
    if (!device) return

    try {
      device.removeAllListeners?.()
    } catch {
      // ignore — best-effort; the device is detached either way
    }
    const destroy = () => {
      try {
        device.destroy?.()
      } catch {
        // ignore — best-effort destroy
      }
    }
    if (!hasActiveCallElsewhere()) {
      destroy()
      return
    }

    console.log(
      '[VoiceCallService] Another tab hosts an active call — device destroy deferred until it ends'
    )
    let unsubscribe = null
    let done = false
    unsubscribe = onExternalOwnerChange(owner => {
      if (owner || done) return
      done = true
      unsubscribe?.()
      destroy()
    })
    // onExternalOwnerChange calls back synchronously; if that call already
    // destroyed the device, `unsubscribe` was not yet assigned inside it.
    if (done) unsubscribe?.()
  }

  mute(next) {
    this.currentConnection?.mute?.(!!next)
  }

  // Microphone-only "hold": mutes THIS agent's microphone and nothing else. The caller is not on hold and still
  // hears anyone else on the call. A real caller hold is the server's setCallerHold (frontendFlags.callerHold,
  // src/utils/callerHold.js; bug rXTNmlfmycTvYm2xp1BS). Surfaces that still call this say what it does.
  hold(isOnHold) {
    if (!this.currentConnection) return false
    try {
      this.currentConnection.mute(!!isOnHold)
      return true
    } catch (e) {
      console.error('[VoiceCallService] Hold failed:', e)
      return false
    }
  }

  isOnHold() {
    return this.currentConnection?.isMuted?.() ?? false
  }
}

export const voiceCallService = new VoiceCallService()
